import React, { Component } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import { isMobile, isTablet } from "react-device-detect";
import { createGlobalStyle } from 'styled-components';
import { withRouter } from "react-router-dom";

import AppRouter from "./router/AppRouter";
import ApiService from './services/ApiService';
import Head from "./components/Head/Head";
import TagService from "./services/TagService";

import UserContext from "./context/UserContext";
import Spinner from './components/Spinner/Spinner';
import Stepbar from "./components/Stepbar/Stepbar";

import { events, screens } from './conf/tags_ga.conf';

import * as defaultWording from './wording';
import * as createWording from './wording/create';
import * as multibalWording from './wording/multibal';
import * as euijaWording from './wording/euija';
import * as recoveryWording from './wording/recovery';
import { MULTIBAL_ACCOUNT } from "./helpers/account";

const GlobalStyle = createGlobalStyle`
  html, body {
    height: 100%;
    margin: 0;
  }
  
  body {
    display: flex;
    flex-direction: column;
  }
 
  footer {
    margin-top: auto;
  }

  // Global spacing class
  .no-spacing-mobile {
    @media (max-width: 480px) {
      margin-top: 0 !important; 
    }
  }
  .spacing-xs {
    margin-top: 15px; 
  }
  .spacing-s {
    margin-top: 30px; 
  }
  .spacing-m {
    margin-top: 45px; 
  }
  .spacing-l {
    margin-top: 60px; 
  }
  .spacing-xl {
    margin-top: 75px; 

  // hack for pro elco
  @media (max-width: 480px) {
    div.opus {
      margin-right: 0;
      margin-left: 0;
    }
  }
`;

export class App extends Component {
  constructor(props) {
    super(props);

    const qs = this.parseQueryString(props.location);

    this.state = {
      qs,
      device: this.getDevice(),
      identities: {},
      contacts: [],
      identitiesId: undefined,
      steps: ['/', '/error'],
      serviceConf: {},
      webViewMode: this.getWebViewMode(),
      isFetching: true,
      wording: defaultWording,
      isChallenge: false,
    };
  }

  async componentDidMount() {
    const { qs } = this.state;

    // Set existing token when detect one
    if (qs.lostid) {
      ApiService.lostId = qs.lostid;
    };
    await this.getAccess();
    this.setState({ isFetching: false });
  }

  setWording = (srv) => {
    switch (srv) {
      case 'euija':
        this.setState({ wording: { ...defaultWording, ...euijaWording } });
        break;
      case 'multibal':
        this.setState({ wording: { ...defaultWording, ...multibalWording } });
        break;
      case 'create':
        this.setState({ wording: { ...defaultWording, ...createWording } });
        break;
      case 'recovery':
        this.setState({ wording: { ...defaultWording, ...recoveryWording } });
        break;
      default:
        this.setState({ wording: defaultWording })
    }
  };

  getStepChoice = (identities, serviceConf, contacts) => {
    const {
      accountLevel,
      hasPostalAddress,
    } = identities;
    const { tempPasswordUrl } = serviceConf;

    if (contacts.length < 1 && !hasPostalAddress && !tempPasswordUrl) {
      return accountLevel === MULTIBAL_ACCOUNT ? '/choice/owner' : '/choice/sav';
    }
    if (contacts.length >= 1 && contacts.find(contact => contact.isDefault)) {
      return '/choice/default';
    }
    return '/choice';
  }

  processIdentities = async (login, captcha) => {
    try {
      const identitiesId = await ApiService.createIdentities({ login, captcha });
      const identities = await ApiService.getIdentities({ identitiesId });
      const serviceConf = await ApiService.getServiceConf();
      const contacts = await ApiService.getIdentitiesContacts({ identitiesId });
      this.setState({
        identitiesId,
        identities,
        serviceConf,
        contacts,
      });
      return { identities, serviceConf, contacts };
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Get user access and redirect to the correct step
   */
  getAccess = async () => {
    const { qs, device } = this.state;
    const { history } = this.props;

    try {
      const res = await ApiService.postAccess({
        qs,
        device,
      });
      const { step, data } = res.data;
      const { identitiesId, login } = data;

      // Set login when exists
      if (login) {
        this.setState(prevState => ({
          identities: {
            ...prevState.identities,
            login,
          },
        }));
      }

      switch (step) {
        case 'password': {
          const identities = await ApiService.getIdentities({ identitiesId });
          const serviceConf = await ApiService.getServiceConf();
          const contacts = await ApiService.getIdentitiesContacts({ identitiesId });
          this.setState({
            identitiesId,
            isChallenge: true,
            identities,
            serviceConf,
            contacts,
          });
          this.setStep('/password');
          history.replace('/password');
          break;
        }
        case 'choice': {
          if (login) {
            TagService.sendEvent(events.bypass_login);
            const { identities, serviceConf, contacts } = await this.processIdentities(login);
            const step = this.getStepChoice(identities, serviceConf, contacts);
            this.setStep(step);
            history.replace(step);
          } else {
            history.replace('/ident');
          }
          break;
        }
        default:
          this.setStep('/ident');
          // keep qs params for Google Analytics campaign purposes
          history.replace('/ident' + this.getFilteredQueryString(qs));
      }
    } catch (err) {
      switch (err.response?.status) {
        case 403:
          // Detect compromised accounts, redirect to recovery
          if (err.response?.data?.code === 403006) {
            const redirectUrl = err.response?.data?.data?.redirectUrl;
            window.location.href = redirectUrl;
            break;
          }
          history.replace({
            pathname: '/error',
            state: {
              code: err.response?.data?.code,
            }
          });
          break;
        case 412:
          // Detect wrong params, redirect with no parameters
          window.location.href = "/";
          break;
        default:
          history.replace({
            pathname: '/error',
            state: {
              code: err.response?.data?.code,
            }
          });
      }
    }
  };

  getFilteredQueryString() {
    const { qs } = this.state;
    const allowedParams = ['didomiConfig.notice.enable', 'utm_campaign', 'utm_medium', 'utm_source'];
    const allowedParamsInQs = Object.keys(qs).filter(param => allowedParams.indexOf(param) !== -1);

    if (allowedParamsInQs.length === 0) {
      return '';
    }

    const filteredQs = {};
    allowedParamsInQs.forEach((param) => filteredQs[param] = qs[param]);
    return '?' + new URLSearchParams(filteredQs).toString();
  }

  /**
   * @param {*} prevProps
   */
  async componentDidUpdate(prevProps, prevState) {
    const { location, history } = this.props;
    const { identities, serviceConf } = this.state;
    const { pathname } = location;

    if (prevProps.location !== location) {
      document.title = this.getHtmlTitles()[pathname];
      window.scrollTo(0, 0);
      // Reset state when navigate to step 1
      if (pathname === '/ident' && history.action === 'POP') {
        this.resetState();
      }
      // Send GA page view on each location update
      TagService.sendPageView({
        'name': screens[location.pathname],
        'parcours_lost': TagService.getAppUseCase({ identities, serviceConf }),
        'canal': isMobile ? "Mobile Web" : "Web",
      });
    }
    // Set dictionary when service conf changed
    if (prevState.serviceConf !== serviceConf) {
      this.setWording(serviceConf.dictionary);
      if (serviceConf.dictionary === 'recovery') {
        document.title = this.getHtmlTitles()[pathname];
      }
    }
  }

  /**
   * Get the html titles based on the current wording (may differ depending on which dictionary is in use)
   * @return {Object} An object with url pathname as keys, and title as values.
   */
  getHtmlTitles() {
    const { wording } = this.state;
    const { WHTML_001, WHTML_002, WHTML_003, WHTML_004, WHTML_005, WHTML_006, WHTML_007, WHTML_008, WHTML_009, WHTML_010, WHTML_011, WHTML_012, WHTML_013 } = wording;
    
    return {
      '/ident': WHTML_001,
      '/choice': WHTML_002,
      '/choice/owner': WHTML_011,
      '/choice/sav': WHTML_012,
      '/choice/default': WHTML_013,
      '/letter': WHTML_003,
      '/letter/pending': WHTML_009,
      '/letter/confirm': WHTML_010,
      '/otc': WHTML_004,
      '/password': WHTML_005,
      '/contact': WHTML_006,
      '/confirm': WHTML_007,
      '/error': WHTML_008,
    };
  }

  /**
   * Parse the query string params in url
   * Split the url without the hashbang ('#!/') when a hash is detected
   * @return {Object} Query String Object
   */
  parseQueryString = (location = {}) => {
    let qs = {};
    const { search, hash } = location;
    if (search) {
      qs = queryString.parse(location.search);
    }
    if (hash) {
      const hashbang = "#!/";
      const url = location.hash.replace(hashbang, "");
      qs = queryString.parse(url);
    }
    return qs;
  };

  /**
   * Get the string device type
   * @return {string}
   */
  getDevice = () => {
    if (isMobile) {
      return "webphone";
    } else if (isTablet) {
      return "tablet";
    } else {
      return "desktop";
    }
  };

  /**
   * Set the meta attributes when the app is in web view mode
   */
  getWebViewMode = () => {
    if (!window.o_confCommon.headerDisplay) {
      const meta = document.createElement("meta");
      meta.setAttribute("http-equiv", "pragma");
      meta.setAttribute("content", "webViewMode:1.0");
      document.getElementsByTagName("head")[0].appendChild(meta);
      return true;
    }
    return false;
  };

  /**
   * Add new autorized pathname
   * @param {String} pathname 
   */
  setStep = (pathname) => {
    const { steps } = this.state;

    if (!steps.find(step => step === pathname)) {
      this.setState(state => ({
        steps: [
          ...state.steps,
          pathname,
        ],
      }));
    }
  };

  /**
   * Reset initial state
   */
  resetState = () => {
    this.setState(state => ({
      ...state,
      identitiesId: null,
      identities: {
        login: state.identities.login,
      },
      steps: ['/', '/ident', '/error'],
    }))
  };

  render() {
    const {
      identities,
      contacts,
      identitiesId,
      wording,
      steps,
      serviceConf,
      webViewMode,
      isFetching,
      isChallenge,
    } = this.state;

    return (
      <UserContext.Provider
        value={{
          identities,
          identitiesId,
          wording,
          steps,
          serviceConf,
          webViewMode,
          isChallenge,
          contacts,
          setStep: this.setStep,
          processIdentities: this.processIdentities,
          getStepChoice: this.getStepChoice,
        }}>
        <GlobalStyle />
        <Head />
        <Stepbar />
        {isFetching ? <Spinner /> : <AppRouter />}
        <div className="spacing-l"></div>
      </UserContext.Provider>
    );
  }
}

App.defaultProps = {
};

App.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(App);
